import React from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';

import type { SetPasswordStage } from '@/types/AuthState';

const SUPPORT_EMAIL = 'support@six3ro.com';
const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}`;

export type SetPasswordErrorMode = 'register' | 'recover' | 'change';

export interface SetPasswordErrorProps {
  stage: SetPasswordStage | undefined;
  message: string | undefined;
  mode: SetPasswordErrorMode;
}

export default function SetPasswordError({ stage, message, mode }: SetPasswordErrorProps) {
  if (message === undefined) return null;

  if (stage === 'postCommit') {
    return (
      <div
        role="alert"
        className="rounded-md border-2 border-destructive bg-destructive/10 p-4 text-sm text-destructive"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <div className="flex flex-col gap-2">
            {mode === 'change' && (
              <>
                <p className="font-semibold">We couldn't confirm your password change</p>
                <p>{message}</p>
              </>
            )}
            {mode === 'recover' && (
              <>
                <p className="font-semibold">Account setup didn't finish</p>
                <p>
                  Your password was changed, but we couldn't finish setting up your account.
                  Please restart the{' '}
                  <Link
                    to="/forgot-password"
                    className="font-semibold underline underline-offset-2"
                  >
                    Forgot Password
                  </Link>{' '}
                  flow. If this keeps happening, contact{' '}
                  <a
                    href={SUPPORT_MAILTO}
                    className="font-semibold underline underline-offset-2"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </p>
              </>
            )}
            {mode === 'register' && (
              <>
                <p className="font-semibold">Account setup didn't finish</p>
                <p>
                  Your password was saved, but we couldn't finish setting up your account.
                  Please contact{' '}
                  <a
                    href={SUPPORT_MAILTO}
                    className="font-semibold underline underline-offset-2"
                  >
                    {SUPPORT_EMAIL}
                  </a>{' '}
                  to complete your registration.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="alert"
      className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
    >
      {message}
    </div>
  );
}
